import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from 'react-intl';

import Checkbox from 'components/ui/Checkbox';
import CheckboxGroup from 'components/ui/CheckboxGroup';
import ValidationForm from 'components/ui/ValidationForm';

import { WRITE_OFF_REASONS } from '../../../../constants/reasons';

import {
  handleAllFeesChange,
  handleReasonChange,
  validateReasonCode,
  validateAdditionalExplanation,
  validateLabel
} from './OverrideFeesForm.utils';

import AlertBox from 'components/ui/AlertBox';

import { SubmitButton, StyledDropdown, StyledForm, StyledLabel, StyledTextArea, OverrideOptions } from './OverrideFeesForm.styles';

const OverrideFeesForm = ({
  intl: { formatMessage },
  onSubmit,
  includeReason,
  includeFareDiff,
  includeOfflineFee,
  includeExchangeFee,
  includeCancellationFee,
  includeHotelCancellationFee,
  includeRepricing,
  waiveFeesWarning
}) => (
  <ValidationForm
    fields={{
      reasonCode: '',
      fareDiff: false,
      offlineFee: false,
      exchangeFee: false,
      repricing: false,
      cancellationFee: false,
      hotelCancellationFee: false,
      additionalExplanation: '',
      label: ''
    }}
    validators={{
      reasonCode: validateReasonCode(includeReason),
      additionalExplanation: validateAdditionalExplanation(),
      label: validateLabel()
    }}
  >
    {({ onChange, resetField, changeField, fields, hasInvalidInputs }) => {
      const {
        reasonCode,
        fareDiff,
        offlineFee,
        exchangeFee,
        additionalExplanation,
        label,
        repricing,
        cancellationFee,
        hotelCancellationFee
      } = fields;

      const includedOverrides = [
        includeOfflineFee,
        includeFareDiff,
        includeExchangeFee,
        includeRepricing,
        includeCancellationFee,
        includeHotelCancellationFee
      ].filter(Boolean);
      const activeOverrides = [offlineFee, fareDiff, exchangeFee, repricing, cancellationFee, hotelCancellationFee].filter(Boolean);

      const hasOverrides = activeOverrides.length > 0;
      const allOverrides = includedOverrides.length === activeOverrides.length;

      const canSubmitOverrides = includedOverrides.length > 0 ? hasOverrides : true;
      const canSubmitReason = includeReason ? Boolean(reasonCode) : true;
      const canSubmit = canSubmitOverrides && canSubmitReason && !hasInvalidInputs;

      const overrideReason = WRITE_OFF_REASONS.find((reason) => reason.code === reasonCode);
      const requiresAdditional = Boolean(overrideReason && overrideReason.requiresAdditional);

      const handleSubmit = (event) => {
        event.preventDefault();
        onSubmit(fields);
      };

      return (
        <StyledForm data-testid="override-fees-form" onSubmit={handleSubmit}>
          {includeReason && (
            <StyledDropdown
              name="reasonCode"
              value={`${reasonCode}_${label}`}
              required
              data-cy="override-fees-reason"
              data-testid="override-fees-reason"
              onChange={handleReasonChange(changeField, resetField, label)}
            >
              <option value="" selected>
                {formatMessage({ id: 'overrideFees.defaultReason' })}
              </option>
              {WRITE_OFF_REASONS.map(({ code, label }) => (
                <option key={code} value={`${code}_${label}`}>
                  {label}
                </option>
              ))}
            </StyledDropdown>
          )}

          {requiresAdditional && (
            <>
              <StyledLabel>
                <FormattedMessage id="overrideFees.explanation" />
              </StyledLabel>

              <StyledTextArea
                name="additionalExplanation"
                value={additionalExplanation}
                required
                rows="3"
                cols="40"
                data-cy="override-fees-additional-explanation"
                onChange={onChange}
                maxLength={37}
              />
            </>
          )}

          <StyledLabel>
            <FormattedMessage id="overrideFees.waiveTitle" />
          </StyledLabel>

          <OverrideOptions includeOverrides={includedOverrides.length > 0}>
            <CheckboxGroup>
              {includedOverrides.length > 1 && (
                <Checkbox
                  checked={allOverrides}
                  onChange={handleAllFeesChange(changeField, {
                    fareDiff: includeFareDiff,
                    offlineFee: includeOfflineFee,
                    exchangeFee: includeExchangeFee,
                    cancellationFee: includeCancellationFee,
                    hotelCancellationFee: includeHotelCancellationFee,
                    repricing: includeRepricing
                  })}
                  name="allFees"
                  dataCy="override-fees-options-all"
                  data-testid="override-fees-options-all"
                  label={<FormattedMessage id="overrideFees.allFees" />}
                />
              )}
              {includeFareDiff && (
                <Checkbox
                  checked={fareDiff}
                  onChange={(checked) => changeField('fareDiff')(checked)}
                  name="fareDiff"
                  dataCy="override-fees-options-fare-diff"
                  label={<FormattedMessage id="overrideFees.fareDiff" />}
                />
              )}
              {includeOfflineFee && (
                <Checkbox
                  checked={offlineFee}
                  onChange={(checked) => changeField('offlineFee')(checked)}
                  name="offlineFee"
                  dataCy="override-fees-options-offline-fee"
                  label={<FormattedMessage id="overrideFees.offlineFee" />}
                />
              )}
              {includeExchangeFee && (
                <Checkbox
                  checked={exchangeFee}
                  onChange={(checked) => changeField('exchangeFee')(checked)}
                  name="exchangeFee"
                  dataCy="override-fees-options-exchange-fee"
                  label={<FormattedMessage id="overrideFees.exchangeFee" />}
                />
              )}
              {includeCancellationFee && (
                <Checkbox
                  checked={cancellationFee}
                  onChange={(checked) => changeField('cancellationFee')(checked)}
                  name="cancellationFee"
                  dataCy="override-fees-options-cancellation-fee"
                  label={<FormattedMessage id="overrideFees.cancellationFee" />}
                />
              )}
              {includeHotelCancellationFee && (
                <Checkbox
                  checked={hotelCancellationFee}
                  onChange={(checked) => changeField('hotelCancellationFee')(checked)}
                  name="hotelCancellationFee"
                  dataCy="override-fees-options-hotel-cancellation-fee"
                  label={<FormattedMessage id="overrideFees.hotelCancellationFee" />}
                />
              )}
              {includeRepricing && (
                <Checkbox
                  checked={repricing}
                  onChange={(checked) => changeField('repricing')(checked)}
                  name="repricing"
                  dataCy="override-fees-options-repricing"
                  label={<FormattedMessage id="overrideFees.repricing" />}
                />
              )}
            </CheckboxGroup>

            <SubmitButton data-cy="override-fees-submit" disabled={!canSubmit}>
              <FormattedMessage id="overrideFees.submit" />
            </SubmitButton>
          </OverrideOptions>
          {waiveFeesWarning && (
            <AlertBox variant={AlertBox.variants.WARNING} dataCy="waive-fees-warning">
              {waiveFeesWarning}
            </AlertBox>
          )}
        </StyledForm>
      );
    }}
  </ValidationForm>
);

OverrideFeesForm.propTypes = {
  intl: intlShape,
  onSubmit: PropTypes.func,
  includeReason: PropTypes.bool,
  includeFareDiff: PropTypes.bool,
  includeOfflineFee: PropTypes.bool,
  includeExchangeFee: PropTypes.bool,
  includeCancellationFee: PropTypes.bool,
  includeHotelCancellationFee: PropTypes.bool,
  includeRepricing: PropTypes.bool,
  waiveFeesWarning: PropTypes.node
};

export default OverrideFeesForm;
